import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
	ZoomIn,
	ZoomOut,
	RotateCw,
	RotateCcw,
	Download,
	X,
} from "lucide-react";
import api from "../../services/api";

const ModalImageCors = ({ imageUrl }) => {
	const [blobUrl, setBlobUrl] = useState("");
	const [erro, setErro] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	// Estados de manipulação da imagem
	const [scale, setScale] = useState(1);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [rotation, setRotation] = useState(0);
	const [isDragging, setIsDragging] = useState(false);

	const containerRef = useRef(null);
	const dragStartRef = useRef({ x: 0, y: 0 });
	const isDraggingRef = useRef(false);
	const hasMovedRef = useRef(false);
	const stateRef = useRef({ scale: 1, position: { x: 0, y: 0 } });

	// Mantém o stateRef atualizado para callbacks de event listeners nativos
	stateRef.current = { scale, position };

	useEffect(() => {
		if (!imageUrl) return undefined;

		let url = "";

		const carregar = async () => {
			try {
				const { data, headers } = await api.get(imageUrl, {
					responseType: "blob",
				});
				url = window.URL.createObjectURL(
					new Blob([data], { type: headers["content-type"] })
				);
				setBlobUrl(url);
			} catch (err) {
				setErro(true);
			}
		};

		carregar();

		return () => {
			if (url) window.URL.revokeObjectURL(url);
		};
	}, [imageUrl]);

	// Reseta zoom, posição e rotação ao abrir/fechar
	const resetTransform = useCallback(() => {
		setScale(1);
		setPosition({ x: 0, y: 0 });
		setRotation(0);
	}, []);

	const handleOpen = () => {
		resetTransform();
		setIsOpen(true);
	};

	const handleClose = useCallback(() => {
		setIsOpen(false);
		resetTransform();
	}, [resetTransform]);

	// Bloqueia o scroll da página enquanto o modal estiver aberto
	useEffect(() => {
		if (!isOpen) return;

		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const handleKeyDown = (e) => {
			if (e.key === "Escape") {
				handleClose();
			} else if (e.key === "+" || e.key === "=") {
				setScale((s) => Math.min(10, +(s * 1.25).toFixed(2)));
			} else if (e.key === "-") {
				setScale((s) => Math.max(0.5, +(s / 1.25).toFixed(2)));
			} else if (e.key === "0") {
				resetTransform();
			} else if (e.key === "r" || e.key === "R") {
				setRotation((r) => (r + 90) % 360);
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			document.body.style.overflow = originalOverflow;
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, handleClose, resetTransform]);

	// Zoom com a rodinha do mouse (Wheel Zoom) com foco no cursor
	useEffect(() => {
		if (!isOpen) return;
		const container = containerRef.current;
		if (!container) return;

		const handleWheel = (e) => {
			e.preventDefault();

			const currentScale = stateRef.current.scale;
			const currentPos = stateRef.current.position;

			// Fator de zoom proporcional
			const zoomFactor = e.deltaY < 0 ? 1.2 : 0.833;
			const newScale = Math.min(Math.max(0.5, +(currentScale * zoomFactor).toFixed(2)), 10);

			if (newScale === currentScale) return;

			// Calcula ponto focal do cursor em relação ao centro do contêiner
			const rect = container.getBoundingClientRect();
			const mouseX = e.clientX - rect.left - rect.width / 2;
			const mouseY = e.clientY - rect.top - rect.height / 2;

			// Ajusta posição para manter o ponto sob o mouse fixo
			const ratio = newScale / currentScale;
			const newX = mouseX - (mouseX - currentPos.x) * ratio;
			const newY = mouseY - (mouseY - currentPos.y) * ratio;

			if (newScale <= 1) {
				// Se voltou ao tamanho normal ou menor, centraliza
				setPosition({ x: 0, y: 0 });
			} else {
				setPosition({ x: newX, y: newY });
			}

			setScale(newScale);
		};

		// passive: false é obrigatório para que e.preventDefault() funcione no wheel
		container.addEventListener("wheel", handleWheel, { passive: false });

		return () => {
			container.removeEventListener("wheel", handleWheel);
		};
	}, [isOpen]);

	// Arrastar (Pan / Drag) com o mouse ou toque
	const handlePointerDown = (e) => {
		// Apenas botão principal (esquerdo) ou toque
		if (e.button !== undefined && e.button !== 0) return;

		isDraggingRef.current = true;
		hasMovedRef.current = false;
		dragStartRef.current = {
			x: e.clientX - position.x,
			y: e.clientY - position.y,
		};
		setIsDragging(true);
	};

	const handlePointerMove = (e) => {
		if (!isDraggingRef.current) return;

		const nextX = e.clientX - dragStartRef.current.x;
		const nextY = e.clientY - dragStartRef.current.y;

		// Se moveu mais de 3px, consideramos que foi arrasto (não apenas clique)
		if (
			Math.abs(nextX - position.x) > 3 ||
			Math.abs(nextY - position.y) > 3
		) {
			hasMovedRef.current = true;
		}

		setPosition({ x: nextX, y: nextY });
	};

	const handlePointerUp = () => {
		isDraggingRef.current = false;
		setIsDragging(false);
	};

	// Duplo clique: alterna rapidamente entre 1x e 2.5x para leitura rápida
	const handleDoubleClick = (e) => {
		e.stopPropagation();
		if (scale > 1.2) {
			resetTransform();
		} else {
			const container = containerRef.current;
			if (!container) {
				setScale(2.5);
				return;
			}
			const rect = container.getBoundingClientRect();
			const mouseX = e.clientX - rect.left - rect.width / 2;
			const mouseY = e.clientY - rect.top - rect.height / 2;
			const targetScale = 2.5;
			const ratio = targetScale / scale;
			const newX = mouseX - (mouseX - position.x) * ratio;
			const newY = mouseY - (mouseY - position.y) * ratio;

			setScale(targetScale);
			setPosition({ x: newX, y: newY });
		}
	};

	// Clique no fundo para fechar (apenas se não tiver arrastado)
	const handleBackdropClick = (e) => {
		if (e.target === containerRef.current && !hasMovedRef.current) {
			handleClose();
		}
	};

	// Botões de zoom manual
	const handleZoomStep = (delta) => {
		setScale((s) => {
			const target = delta > 0 ? s * 1.3 : s / 1.3;
			const newScale = Math.min(Math.max(0.5, +target.toFixed(2)), 10);
			if (newScale <= 1) {
				setPosition({ x: 0, y: 0 });
			}
			return newScale;
		});
	};

	// Girar 90 graus
	const handleRotate = () => {
		setRotation((r) => (r + 90) % 360);
	};

	// Download do arquivo original
	const handleDownload = (e) => {
		e.stopPropagation();
		const link = document.createElement("a");
		link.href = blobUrl;
		const fileName =
			(imageUrl && imageUrl.split("/").pop()) || "documento_anexo.png";
		link.download = decodeURIComponent(fileName);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	if (erro) {
		return (
			<div className="flex h-[200px] w-[250px] items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">
				Não foi possível carregar a imagem
			</div>
		);
	}

	if (!blobUrl) {
		return (
			<div className="h-[200px] w-[250px] animate-pulse rounded-lg bg-muted" />
		);
	}

	return (
		<>
			{/* Thumbnail no balão da conversa */}
			<div
				role="button"
				tabIndex={0}
				onClick={handleOpen}
				onKeyDown={(e) => e.key === "Enter" && handleOpen()}
				className="group relative h-[200px] w-[250px] cursor-pointer overflow-hidden rounded-lg shadow-sm transition-transform hover:opacity-95"
				title="Clique para abrir e dar zoom"
			>
				<img
					src={blobUrl}
					alt="Imagem enviada na conversa"
					className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
				/>
				<div className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
					<div className="flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur-md">
						<ZoomIn className="h-3.5 w-3.5" />
						<span>Ampliar</span>
					</div>
				</div>
			</div>

			{/* Modal Lightbox com Zoom via Scroll e Arrastar */}
			{isOpen &&
				createPortal(
					<div className="fixed inset-0 z-[9999] flex flex-col bg-black/90 backdrop-blur-md select-none animate-in fade-in duration-150">
						{/* Barra Superior de Controles */}
						<div className="relative z-10 flex items-center justify-between border-b border-white/10 bg-black/50 px-4 py-2.5 backdrop-blur-sm">
							<div className="flex items-center gap-3">
								<span className="text-sm font-medium text-white/90">
									Imagem enviada na conversa
								</span>
								<span className="rounded-full border border-white/10 bg-white/10 px-2.5 py-0.5 font-mono text-xs text-white/80">
									{Math.round(scale * 100)}%
								</span>
								{rotation !== 0 && (
									<span className="rounded-full border border-white/10 bg-white/10 px-2.5 py-0.5 font-mono text-xs text-white/80">
										{rotation}°
									</span>
								)}
							</div>

							{/* Ações / Ferramentas */}
							<div className="flex items-center gap-1 rounded-full border border-white/10 bg-neutral-900/90 p-1 shadow-lg">
								<button
									type="button"
									onClick={() => handleZoomStep(-1)}
									className="rounded-full p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
									title="Reduzir zoom (-)"
								>
									<ZoomOut className="h-4 w-4" />
								</button>
								<button
									type="button"
									onClick={() => handleZoomStep(1)}
									className="rounded-full p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
									title="Aumentar zoom (+)"
								>
									<ZoomIn className="h-4 w-4" />
								</button>
								<button
									type="button"
									onClick={resetTransform}
									className="rounded-full p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
									title="Resetar tamanho original (0)"
								>
									<RotateCcw className="h-4 w-4" />
								</button>

								<div className="mx-1 h-4 w-px bg-white/20" />

								<button
									type="button"
									onClick={handleRotate}
									className="rounded-full p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
									title="Girar 90° (R)"
								>
									<RotateCw className="h-4 w-4" />
								</button>
								<button
									type="button"
									onClick={handleDownload}
									className="rounded-full p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
									title="Baixar imagem"
								>
									<Download className="h-4 w-4" />
								</button>

								<div className="mx-1 h-4 w-px bg-white/20" />

								<button
									type="button"
									onClick={handleClose}
									className="rounded-full p-2 text-white/80 transition-colors hover:bg-red-500/30 hover:text-red-300"
									title="Fechar (ESC)"
								>
									<X className="h-4 w-4" />
								</button>
							</div>
						</div>

						{/* Área Principal de Visualização e Zoom */}
						<div
							ref={containerRef}
							onClick={handleBackdropClick}
							onPointerDown={handlePointerDown}
							onPointerMove={handlePointerMove}
							onPointerUp={handlePointerUp}
							onPointerCancel={handlePointerUp}
							className={`relative flex flex-1 items-center justify-center overflow-hidden ${
								scale > 1
									? isDragging
										? "cursor-grabbing"
										: "cursor-grab"
									: "cursor-zoom-in"
							}`}
						>
							<img
								src={blobUrl}
								alt="Imagem enviada na conversa"
								draggable={false}
								onDoubleClick={handleDoubleClick}
								className="max-h-[85vh] max-w-[90vw] select-none object-contain will-change-transform"
								style={{
									transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale}) rotate(${rotation}deg)`,
									transition: isDragging ? "none" : "transform 150ms cubic-bezier(0.2, 0, 0, 1)",
								}}
							/>
						</div>

						{/* Dica discreta no rodapé */}
						<div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-white/10 bg-neutral-900/80 px-4 py-1.5 text-xs text-white/70 shadow-lg backdrop-blur-md">
							<span>Role o mouse para zoom</span>
							<span className="mx-2 opacity-40">•</span>
							<span>Arraste para mover</span>
							<span className="mx-2 opacity-40">•</span>
							<span>Duplo clique: 2.5x</span>
							<span className="mx-2 opacity-40">•</span>
							<span>R: girar</span>
						</div>
					</div>,
					document.body
				)}
		</>
	);
};

export default ModalImageCors;
